import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X, User, LogOut, Home } from "lucide-react";
import { CoursesMenu, MobileCoursesMenu } from "./CoursesMenu";

// Logo URLs
const LOGO_FULL = "https://customer-assets.emergentagent.com/job_75937e85-5d04-4ca2-9da1-90520b393fdf/artifacts/yfop01nt_VOTRA_logo_transparent_large.png";

// Session timeout in milliseconds (15 minutes)
const SESSION_TIMEOUT = 15 * 60 * 1000;

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const navLinks = [
    { path: "/", label: "Kurser" },
    { path: "/kalender", label: "Kursuskalender" },
    { path: "/om-os", label: "Om os" },
    { path: "/ledige", label: "Ledige" },
    { path: "/jobcentre", label: "Jobcentre & A-kasser" },
    { path: "/partner", label: "Partnerprogram" },
    { path: "https://blog.votra.dk/blog?lang=da", label: "Blog" },
  ];
  const goTo = (path) => (path.startsWith("http") ? window.location.assign(path) : navigate(path));

  const isActive = (path) => location.pathname === path;

  // Check for logged in user and session validity
  useEffect(() => {
    checkSession();
    
    // Set up interval to check session every minute
    const interval = setInterval(checkSession, 60000);
    return () => clearInterval(interval);
  }, []);

  const checkSession = () => {
    const token = localStorage.getItem('minskoleToken');
    const userJson = localStorage.getItem('minskoleUser');
    const loginTime = localStorage.getItem('minskoleLoginTime');
    
    if (token && userJson && loginTime) {
      const elapsed = Date.now() - parseInt(loginTime, 10);
      
      if (elapsed > SESSION_TIMEOUT) {
        // Session expired - logout
        handleLogout(true);
      } else {
        // Session valid
        try {
          setUser(JSON.parse(userJson));
        } catch (e) {
          setUser(null);
        }
      }
    } else {
      setUser(null);
    }
  };

  const handleLogout = (sessionExpired = false) => {
    localStorage.removeItem('minskoleToken');
    localStorage.removeItem('minskoleUser');
    localStorage.removeItem('minskoleLoginTime');
    localStorage.removeItem('adminToken');
    localStorage.removeItem('studentToken');
    setUser(null);
    setShowUserMenu(false);
    
    if (sessionExpired) {
      // Store that session expired for showing message on login page
      sessionStorage.setItem('sessionExpired', 'true');
    }
    
    navigate('/');
  };

  const handleLoginClick = () => {
    const token = localStorage.getItem('minskoleToken');
    const loginTime = localStorage.getItem('minskoleLoginTime');
    
    if (token && loginTime) {
      const elapsed = Date.now() - parseInt(loginTime, 10);
      
      if (elapsed < SESSION_TIMEOUT) {
        // Still within session - go directly to dashboard
        const userJson = localStorage.getItem('minskoleUser');
        if (userJson) {
          const userData = JSON.parse(userJson);
          if (userData.user_type === 'student') {
            navigate('/student');
          } else {
            navigate('/minskole/dashboard');
          }
          return;
        }
      }
    }
    
    // No valid session - go to login
    navigate('/minskole');
  };

  return (
    <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-50">
      {/* Responsive container with different heights per breakpoint */}
      <div className="h-[70px] sm:h-[80px] md:h-[90px] lg:h-[100px] px-4 sm:px-6 md:px-8 lg:px-10 flex items-center justify-between max-w-[1920px] mx-auto">
        
        {/* Logo - responsive sizing */}
        <div 
          className="flex items-center cursor-pointer flex-shrink-0"
          onClick={() => navigate('/')}
        >
          {/* Large screens: Full logo */}
          <img 
            src={LOGO_FULL} 
            alt="Votra - Vognmandsuddannelser" 
            className="hidden lg:block h-[80px] w-auto"
          />
          {/* Medium screens (tablets): Full logo smaller */}
          <img 
            src={LOGO_FULL} 
            alt="Votra - Vognmandsuddannelser" 
            className="hidden md:block lg:hidden h-[60px] w-auto"
          />
          {/* Small screens (mobile): Full logo (same as desktop website, scaled down) */}
          <img 
            src={LOGO_FULL} 
            alt="Votra - Vognmandsuddannelser" 
            className="md:hidden h-[42px] sm:h-[52px] w-auto"
          />
        </div>

        {/* Desktop Navigation - hidden on tablet and mobile */}
        <nav className="hidden xl:flex items-center gap-1">
          <CoursesMenu active={isActive("/") || isActive("/kurser")} onGo={navigate} />
          {navLinks.slice(1).map((link) => (
            <button
              key={link.path}
              onClick={() => goTo(link.path)}
              data-testid={`header-nav-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                isActive(link.path)
                  ? 'bg-[hsl(210,100%,50%)]/10 text-[hsl(210,100%,45%)]'
                  : 'text-slate-600 hover:text-[hsl(210,100%,45%)] hover:bg-slate-50'
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* Right Section */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Contact button - hidden on small screens */}
          <button
            onClick={() => navigate('/kontakt')}
            className="hidden lg:block text-sm px-3 lg:px-4 py-2 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50 transition-colors"
          >
            Kontakt
          </button>
          
          {user ? (
            /* Logged in user menu */
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 text-sm px-3 sm:px-4 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
              >
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">{user.name || user.user_id}</span>
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      navigate('/');
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <Home className="h-4 w-4" /> Til hjemmesiden
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      if (user.user_type === 'student') {
                        navigate('/student');
                      } else {
                        navigate('/minskole/dashboard');
                      }
                    }}
                    className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                  >
                    <User className="h-4 w-4" /> Min profil
                  </button>
                  <hr className="my-2 border-slate-100" />
                  <button
                    onClick={() => handleLogout(false)}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                  >
                    <LogOut className="h-4 w-4" /> Log ud
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Login button - Mit VOTRA */
            <button
              onClick={handleLoginClick}
              className="text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg text-white hover:opacity-90 transition-colors whitespace-nowrap"
              style={{ backgroundColor: 'hsl(210, 100%, 45%)' }}
              data-testid="minskole-login-link"
            >
              Mit VOTRA
            </button>
          )}

          {/* Business button - white bg with gold border */}
          <button
            onClick={() => navigate('/business')}
            className="hidden sm:inline-flex items-center text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg bg-white font-medium whitespace-nowrap transition-all hover:shadow-md"
            style={{ border: '2px solid #D4AF37', color: '#8B7500' }}
            data-testid="business-nav-btn"
          >
            Business
          </button>
          
          {/* Mobile/Tablet Menu Button - visible on screens smaller than xl */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden p-2 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg"
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X className="h-5 w-5 sm:h-6 sm:w-6" /> : <Menu className="h-5 w-5 sm:h-6 sm:w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile/Tablet Navigation Dropdown */}
      {mobileMenuOpen && (
        <nav className="xl:hidden absolute left-0 right-0 top-full bg-white border-b border-slate-200 shadow-lg z-50">
          <div className="max-w-[1920px] mx-auto px-4 sm:px-6 py-4">
            <div className="flex flex-col gap-1">
              <MobileCoursesMenu onGo={(path) => { navigate(path); setMobileMenuOpen(false); }} />
              {navLinks.slice(1).map((link) => (
                <button
                  key={link.path}
                  onClick={() => {
                    goTo(link.path);
                    setMobileMenuOpen(false);
                  }}
                  className={`px-4 py-3 rounded-lg text-sm sm:text-base font-medium text-left transition-colors ${
                    isActive(link.path)
                      ? 'bg-[hsl(210,100%,50%)]/10 text-[hsl(210,100%,45%)]'
                      : 'text-slate-600 hover:text-[hsl(210,100%,45%)] hover:bg-slate-50'
                  }`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => {
                  navigate('/kontakt');
                  setMobileMenuOpen(false);
                }}
                className="px-4 py-3 rounded-lg text-sm sm:text-base font-medium text-left text-slate-600 hover:text-[hsl(210,100%,45%)] hover:bg-slate-50"
              >
                Kontakt
              </button>
              <button
                onClick={() => {
                  navigate('/business');
                  setMobileMenuOpen(false);
                }}
                className="mt-2 px-4 py-3 rounded-lg text-sm sm:text-base font-semibold text-center bg-white transition-all hover:shadow-md"
                style={{ border: '2px solid #D4AF37', color: '#8B7500' }}
                data-testid="business-nav-btn-mobile"
              >
                Business
              </button>
            </div>
          </div>
        </nav>
      )}
      
      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
    </header>
  );
};

export default Header;
